import React, { useMemo, useState, Fragment, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Box, Typography, Checkbox, CircularProgress, Button, Link } from "@material-ui/core"
import GreenButton from "../../Common/GreenButton/index"
import moment from "moment"
import CheckIcon from "@material-ui/icons/Check"
import calculateTotals from "../../../../utils/calculateTotals"
import formatNumberWithCommas from "../../../../utils/formatNumberWithCommas"

import {
  toggleClientInvoiceLogsDialog,
  toggleBillingAdjustmentDialog,
} from "../../../../actions/billingDialogs"

import classNames from "classnames"
import useStyles from "./styles"
import { markPaymentReceived } from "../../../../actions/billing"
import { openSnackBar } from "../../../../actions/snack-bar"
import usePaidAmountInvoice from "../../../../hooks/usePaidAmountInvoice"

const InvoiceStatusCard = () => {
  const classes = useStyles()
  const dispatch = useDispatch()

  const [checkboxLoading, toggleCheckboxLoading] = useState(false)

  const generalData = useSelector(state => state.billing.generalData)
  const usersRolesArray = useSelector(state => state.billing.usersRolesArray)

  const acceptedInvoice = generalData && Boolean(generalData.invoice_accepted)
  const paymentTerms = generalData && generalData.payment_terms_name
  const discount = (generalData && generalData.discount) || 0
  const logsCount = generalData.payment_logs.length;
  const payment_logs = generalData.payment_logs;

  const depositRequired = generalData && Boolean(generalData.deposit_required)

  const checked = depositRequired
    ? Boolean(generalData.second_paid)
    : Boolean(generalData.full_paid)

  const paidAmt = usePaidAmountInvoice()

  const finalPaymentAccepted =
    Boolean(generalData.second_paid) || Boolean(generalData.full_paid)

  const paymentType = depositRequired
    ? generalData.second_payment_type
    : generalData.first_payment_type

  const amounts = useMemo(
    () => {
      // copy of usersrolesarray where we exclude not billable expenses
      let newUsersRolesArray = usersRolesArray.map(role => {
        let newRole = { ...role }
        return newRole
      })
      return calculateTotals(newUsersRolesArray, paidAmt, discount)
    },
    [usersRolesArray, paidAmt, discount]
  )

  const markCheckAsReceived = () => {
    toggleCheckboxLoading(true)
    let invoiceId = generalData.client_invoice_id
    let depositRequired = generalData.deposit_required
    let callData = {
      paidColumn: depositRequired ? "second_paid" : "full_paid",
      paymentStatus: checked ? 0 : 1,
    }
    let updateData
    if (depositRequired) {
      updateData = {
        second_paid: checked ? 0 : 1,
        second_paid_date: checked ? null : moment().format(),
      }
    } else {
      updateData = {
        full_paid: checked ? 0 : 1,
        full_paid_date: checked ? null : moment().format(),
        shoot_id: generalData && generalData.id,
      }
    }

    dispatch(markPaymentReceived(callData, invoiceId, updateData))
      .then(() => {
        toggleCheckboxLoading(false)
        dispatch(
          openSnackBar({
            message: `Marked final payment as ${checked ? "not received" : "received"
              }`,
          })
        )
      })
      .catch(err => {
        console.log(err)
        toggleCheckboxLoading(false)
        dispatch(
          openSnackBar({
            message: "Unable to change final payment received status",
          })
        )
      })
  }

  return (
    <Box mb={2} mt={2}>
      <Box display="flex" flexDirection="column" mb={3}>
        <Box className={classes.rowWrapper}>
          <Typography style={{ fontSize: "18px", fontWeight: 600 }}>
            {acceptedInvoice ? "Invoice Accepted" : "Pending Approval"}
          </Typography>
          <Box
            className={classNames(classes.statusDot, {
              [classes.acceptedDot]: acceptedInvoice,
            })}
          >
            {acceptedInvoice && <CheckIcon className={classes.checkIcon} />}
          </Box>
        </Box>
        <Typography className={classes.grayFont} style={{ fontWeight: 700 }}>
          {paymentTerms} Billing
        </Typography>
        {!acceptedInvoice ? (
          <Typography className={classes.grayFont}>
            Sent{" "}
            {moment(generalData.invoice_sent_date_time).format(
              "M/D/YY[ @ ]h:mm A"
            )}
          </Typography>
        ) : (
          <Typography className={classes.grayFont}>
            Accepted{" "}
            {moment(generalData.invoice_accepted_date_time).format(
              "M/D/YY[@]h:mm A"
            )}{" "}
            by {generalData.client_invoice_signature}
          </Typography>
        )}
        {logsCount > 0 && checked && (
          <Box display="flex" justifyContent="space-between">
            <Typography className={classes.grayFont} style={{ pr: "5px" }}>
              This Invoice has been Adjusted
            </Typography>
            <Link
              component="button"
              variant="body2"
              onClick={() => {
                dispatch(toggleClientInvoiceLogsDialog(true, payment_logs))
              }}
            >
              <Typography variant="h4">
                See Adjustment Memo
              </Typography>
            </Link>
          </Box>
        )}

      </Box>
      {/* bottom part */}
      <Box display="flex" flexDirection="column">
        <Box className={classes.rowWrapper}>
          <Typography
            style={{ fontSize: "16px", fontWeight: 600, textAlign: "left" }}
          >
            Final Payment Received
          </Typography>
          <Box
            className={classNames(classes.statusDot, {
              [classes.acceptedDot]: finalPaymentAccepted,
            })}
          >
            {finalPaymentAccepted && (
              <CheckIcon className={classes.checkIcon} />
            )}
          </Box>
        </Box>

        <Box mt={1} className={classes.rowWrapper}>
          <Typography className={classes.blueFont}>Balance Due:</Typography>
          <Typography className={classes.blueFont}>
            $ {formatNumberWithCommas(amounts.original + amounts.additional)}
          </Typography>
        </Box>


        <Box mt={1} className={classes.rowWrapper}>
          <Typography className={classes.redFont}>Non Billable:</Typography>
          <Typography className={classes.redFont}>
            - $ {formatNumberWithCommas(amounts.original - amounts.payAble)}

          </Typography>
        </Box>

        <Box mt={1} className={classes.rowWrapper}>
          <Typography className={classes.redFont}>Deposit:</Typography>
          <Typography className={classes.redFont}>
            - $ {formatNumberWithCommas(amounts.paid)}
          </Typography>

        </Box>



        <Box mt={1} className={classes.rowWrapper}>
          <Typography className={classes.greenFont}>Remaining:</Typography>
          <Typography className={classes.greenFont}>
            $ {formatNumberWithCommas(amounts.remaining)}
          </Typography>
        </Box>

        {(generalData.first_half_amount > 0 || generalData.full_amount > 0) && (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="flex-start"
            textAlign="left"
          >
            {/* variable text based on payment type below: */}
            {paymentType === "credit-card" && (
              <Typography className={classes.grayFont}>
                Card ending in XXXX
              </Typography>
            )}
            {paymentType === "check" && (
              <Fragment>
                <Typography className={classes.grayFont}>Check</Typography>
                <Box
                  mt={2}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex"
                  height="25px"
                >
                  {checkboxLoading ? (
                    <Box pl={2} pr={2} pt={1.5} pb={1.5}>
                      <CircularProgress size={16} />
                    </Box>
                  ) : (
                    (!logsCount || logsCount === 0) &&
                    <Box display="flex">
                      <Checkbox
                        checked={checked}
                        onChange={markCheckAsReceived}
                      />
                      <Typography className={classes.grayFont}>
                        Mark payment{" "}
                        {(generalData && generalData.second_paid) ||
                          generalData.full_paid
                          ? "not received"
                          : "received"}
                      </Typography>
                    </Box>

                  )}
                  {checked && generalData.full_paid && (<Button
                    variant="contained"
                    size="small"
                    style={{
                      display: "flex",
                      border: "01px solid #EBEBEB",
                      borderRadius: "10px",
                      backgroundColor: "#AE0001",
                      color: "#fff",
                      flexWrap: "none",
                    }}
                    onClick={() => dispatch(toggleBillingAdjustmentDialog(true, { billingData: amounts }))}
                  >
                    Adjust Payments
                  </Button>)}

                </Box>
              </Fragment>
            )
            }
            {/* <Typography className={classes.grayFont}>
                  Paid By: generalData.client_estimate_signature
                </Typography> */}
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default InvoiceStatusCard
